import React from 'react';
import {
  Box,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Text,
  Button,
  BankIcon,
  Divider,
  useToast,
} from '@razorpay/blade/components';
import api from 'api';
import { useMutation, useQueryClient } from 'react-query';
import { FETCH_BANK_ACCOUNTS, SEND_FOR_APPROVAL } from 'api/employeeBankAccount';
import { AppError } from 'utils/AppError';
import { parseError } from 'utils/Api';

export const SendForApprovalModal = ({
  isOpen,
  onDismiss,
  accountDetails,
}: {
  isOpen: boolean;
  onDismiss: () => void;
  accountDetails: {
    bankAccountId: number | string;
    beneficiaryName: string;
    accountNumber: string;
    ifscCode: string;
  };
}) => {
  const { beneficiaryName, accountNumber, ifscCode, bankAccountId } = accountDetails || {};
  const toast = useToast();
  const queryClient = useQueryClient();

  const mutateSendForApproval = useMutation({
    mutationKey: SEND_FOR_APPROVAL,
    mutationFn: api.employeeBankAccount.sendForApproval,
    onSuccess: () => {
      onDismiss();
      toast.show({
        content: 'Bank Account Change Request Submitted',
        color: 'information',
      });
      queryClient.invalidateQueries([FETCH_BANK_ACCOUNTS]);
    },
    onError: (e: typeof AppError) => {
      toast.show({
        content: parseError(e),
        color: 'negative',
      });
      onDismiss();
    },
  });

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} size="small">
      <ModalHeader
        title="Bank Account Verified!"
        subtitle="The bank details have been validated, with the following details:"
      />
      <ModalBody>
        <Box
          padding="spacing.5"
          borderRadius="large"
          borderColor="surface.border.gray.subtle"
          borderWidth="thin">
          <Box marginBottom="spacing.3" display="flex" alignItems="center" gap="spacing.3">
            <BankIcon color="surface.icon.gray.subtle" />
            <Text weight="semibold" size="small">
              A/c no: {accountNumber}
            </Text>
          </Box>
          <Box display="flex" alignItems="center" gap="spacing.3">
            <Divider variant="normal" marginX="spacing.3" width="thin" orientation="vertical" />
            <Box display="flex" flexDirection="column" gap="spacing.2">
              <Text size="small" color="surface.text.gray.muted">
                IFSC: {ifscCode}
              </Text>
              <Text size="small" color="surface.text.gray.muted">
                Account Holder Name: {beneficiaryName}
              </Text>
            </Box>
          </Box>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" gap="spacing.5" justifyContent="flex-end">
          <Button variant="tertiary" onClick={onDismiss}>
            Close
          </Button>
          <Button
            isLoading={mutateSendForApproval.isLoading}
            onClick={() => mutateSendForApproval.mutate({ bankAccountId })}>
            Send for approval
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
